import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Box,
  Button,
  Collapse,
  Link,
  List,
  ListItem,
  ListItemText,
  Paper,
  Typography,
} from '@mui/material';
import ReactMarkdown from 'react-markdown';
import changelogPath from '../content/changelog.md';
import './SupportPage.css';
import { upcomingFeatures } from '../content/upcomingFeatures';

const bugReportEmail = 'jessbarringer.dev@gmail.com';
const bugReportSubject = encodeURIComponent('KajukenbAPP Bug Report');
const bugReportHref = `mailto:${bugReportEmail}?subject=${bugReportSubject}`;
const featureRequestSubject = encodeURIComponent('KajukenbAPP Feature Request');
const featureRequestHref = `mailto:${bugReportEmail}?subject=${featureRequestSubject}`;

export default function SupportPage() {
  const location = useLocation();
  const [changelogMarkdown, setChangelogMarkdown] = useState('');
  const [isChangelogOpen, setIsChangelogOpen] = useState(() => location.hash === '#changelog');

  useEffect(() => {
    let isMounted = true;

    async function loadChangelog() {
      try {
        const response = await fetch(changelogPath);
        const markdown = await response.text();

        if (isMounted) {
          setChangelogMarkdown(markdown);
        }
      } catch {
        if (isMounted) {
          setChangelogMarkdown('# Changelog\n\nUnable to load the changelog right now.');
        }
      }
    }

    loadChangelog();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    if (location.hash !== '#changelog') {
      return;
    }

    setIsChangelogOpen(true);
    window.requestAnimationFrame(() => {
      document.getElementById('changelog')?.scrollIntoView({ block: 'start' });
    });
  }, [location.hash]);

  return (
    <Box className="upcoming-features-page">
      <Paper className="upcoming-features-card" elevation={1}>
        <Typography variant="h5" component="h1" gutterBottom>
          Support
        </Typography>

        <Box className="upcoming-features-sections">
          <Box className="feedback-panels">
            <section className="content-panel bug-report-panel" aria-labelledby="bug-report-heading">
              <Typography variant="h6" component="h2" id="bug-report-heading" gutterBottom>
                Bug Reports
              </Typography>
              <Typography variant="body2" color="text.secondary" className="section-intro">
                Found something broken? Send a note with steps, screenshots, and device details so
                it is easier to reproduce.
              </Typography>
              <Link 
                href={bugReportHref} 
                underline="hover" 
                className="action-link bug-report-link" 
                sx={{ mt: 1.5 }}
              >
                Email a bug report
              </Link>
            </section>

            <section
              className="content-panel feature-request-panel"
              aria-labelledby="feature-request-heading"
            >
              <Typography variant="h6" component="h2" id="feature-request-heading" gutterBottom>
                Feature Requests
              </Typography>
              <Typography variant="body2" color="text.secondary" className="section-intro">
                Have an idea that would make the app more useful? Share the problem you are trying
                to solve and what you would like to see added.
              </Typography>
              <Link
                href={featureRequestHref}
                underline="hover"
                className="action-link feature-request-link"
                sx={{ mt: 1.5 }}
              >
                Email a feature request
              </Link>
            </section>
          </Box>

          <section className="content-panel roadmap-panel" aria-labelledby="roadmap-heading">
            <Typography variant="h4" component="h2" id="roadmap-heading" className="roadmap-heading" gutterBottom>
              Upcoming Features
            </Typography>
            <Typography variant="body2" color="text.secondary" className="section-intro">
              A quick look at what is currently on deck.
            </Typography>
            <List className="upcoming-features-list">
              {upcomingFeatures.map((feature) => (
                <ListItem key={feature.title} disableGutters className="upcoming-features-item">
                  <ListItemText
                    primary={feature.title}
                    secondary={feature.description}
                    primaryTypographyProps={{ fontWeight: 600 }}
                  />
                </ListItem>
              ))}
            </List>
          </section>

          <section id="changelog" className="content-panel changelog-panel" aria-labelledby="changelog-heading">
            <Box className="changelog-header">
              <Typography variant="h4" component="h2" id="changelog-heading" className="roadmap-heading">
                Changelog
              </Typography>
              <Button
                variant="text"
                className="changelog-toggle"
                onClick={() => setIsChangelogOpen((isOpen) => !isOpen)}
              >
                {isChangelogOpen ? 'Hide changelog' : 'Show changelog'}
              </Button>
            </Box>
            <Typography variant="body2" color="text.secondary" className="section-intro">
              Recent updates managed from markdown so new entries are easy to add.
            </Typography>
            <Collapse in={isChangelogOpen}>
              <Box className="changelog-markdown" id="changelog-content">
                <ReactMarkdown>{changelogMarkdown}</ReactMarkdown>
              </Box>
            </Collapse>
          </section>

          <Link href="/privacy-policy" underline="hover" className="action-link legal-link" sx={{ mt: 1.5 }}>
            View privacy policy
          </Link>

        </Box>
      </Paper>
    </Box>
  );
}
